import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Users, Phone, Check, Wallet, GraduationCap, HandCoins } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { SITE_URL } from "../data/siteContent";

const COVERS = [
  "Tuition and academic instruction by qualified nursing faculty",
  "Laboratory access — pre-clinical, nursing foundation, advance and computer labs",
  "Library and digital learning resources",
  "Supervised clinical postings at partner hospitals",
  "Uniforms and clinical kit guidance",
  "Examination and university registration support",
];

const SUPPORT = [
  {
    icon: Wallet,
    t: "Flexible payments",
    d: "Instalment-friendly fee schedules are available so families can plan comfortably across the academic year.",
  },
  {
    icon: GraduationCap,
    t: "Scholarship assistance",
    d: "Our office assists eligible students in applying for Government of Jharkhand and other scholarship schemes.",
  },
  {
    icon: HandCoins,
    t: "Education loan guidance",
    d: "We provide the admission documents and guidance needed to apply for education loans with leading banks.",
  },
];

export default function Fees() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "Admissions", item: SITE_URL + "/admissions" },
        { "@type": "ListItem", position: 3, name: "Fees", item: SITE_URL + "/admissions/fees" },
      ],
    },
  ];

  return (
    <div data-testid="fees-page">
      <SEO
        title="Nursing Course Fees in Bokaro — B.Sc, GNM & ANM | SVS College of Nursing"
        description="Fee guidance for B.Sc Nursing, GNM and ANM at SVS College of Nursing, Bokaro. Contact our admissions team for the latest fee structure, instalments, scholarships and education loan support."
        path="/admissions/fees"
        jsonLd={jsonLd}
      />

      <PageHero
        overline="Admissions 2026-27"
        title={<>Fees &amp; <span className="italic text-gold-gradient">financial</span> guidance</>}
        subtitle="Transparent, instalment-friendly fees for every program — with scholarship assistance and education-loan guidance from our admissions office."
      />

      {/* Program fee cards */}
      <section className="bg-sand py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Programs</span>
            <h2 className="font-serif text-4xl text-navy mt-4 mb-12">Fee structure by program</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {courses.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.08}>
                <div className="bg-white border border-navy/10 rounded-[2px] p-8 h-full flex flex-col" data-testid={`fee-card-${c.short_name}`}>
                  <div className="font-serif text-4xl text-gold/40">{c.short_name}</div>
                  <h3 className="font-serif text-2xl text-navy mt-4">{c.title}</h3>
                  <div className="mt-4 space-y-2 text-sm text-navy/60">
                    <span className="flex items-center gap-2"><Clock size={16} className="text-gold" /> {c.duration}</span>
                    <span className="flex items-center gap-2"><Users size={16} className="text-gold" /> {c.seats}</span>
                  </div>
                  <div className="mt-6 bg-navy rounded-[2px] p-5 text-sand relative overflow-hidden">
                    <div className="grain-overlay" />
                    <div className="relative">
                      <div className="text-xs tracking-[0.2em] uppercase text-sand/60">Fee details</div>
                      <p className="text-sm mt-2 leading-relaxed text-sand/85">
                        Shared personally on enquiry — call our admissions team for the current
                        fee schedule and instalment plan.
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/admissions"
                    data-testid={`fee-enquire-${c.short_name}`}
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy font-bold px-6 py-3.5 transition-transform duration-300 hover:scale-[1.03]"
                  >
                    Enquire now <ArrowUpRight size={16} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 text-sm text-navy/50 max-w-2xl">
              Fees are revised periodically by the affiliating university and regulatory bodies.
              The figures shared by our admissions office at the time of enquiry are the applicable ones.
            </p>
          </Reveal>
        </div>
      </section>

      {/* What fees cover */}
      <section className="bg-white py-24" data-testid="fees-cover">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Included</span>
              <h2 className="font-serif text-4xl text-navy mt-4 leading-tight">
                What your fees <span className="italic text-gold-gradient">typically cover</span>
              </h2>
              <p className="text-navy/60 mt-5 leading-relaxed">
                One comprehensive program fee keeps your focus on learning. Exact inclusions are
                confirmed with your personalised fee sheet.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-4">
              {COVERS.map((c, i) => (
                <Reveal key={c} delay={i * 0.05}>
                  <li className="flex gap-4 items-center bg-sand border border-navy/10 rounded-[2px] p-5">
                    <Check size={20} className="text-gold shrink-0" />
                    <span className="text-navy/80">{c}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Financial support */}
      <section className="bg-sand py-24" data-testid="fees-support">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Support</span>
              <h2 className="font-serif text-4xl text-navy mt-4">Financial support, every step</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {SUPPORT.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.08}>
                <div className="bg-navy text-sand rounded-[2px] p-8 h-full relative overflow-hidden" data-testid={`support-${i}`}>
                  <div className="grain-overlay" />
                  <div className="relative">
                    <s.icon size={34} className="text-gold" strokeWidth={1.4} />
                    <h3 className="font-serif text-2xl mt-6 mb-3">{s.t}</h3>
                    <p className="text-sand/70 text-sm leading-relaxed">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-20 relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative max-w-2xl mx-auto px-5 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-sand mb-4">Get your personalised fee sheet</h2>
          <p className="text-sand/70 mb-8">
            Speak to our counsellors for the current fee structure, instalment options and
            scholarship eligibility for your program.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/admissions"
              data-testid="fees-cta-enquire"
              className="inline-flex items-center gap-2 rounded-full bg-gold text-navy font-bold px-8 py-4 transition-transform duration-300 hover:scale-105"
            >
              Submit an enquiry <ArrowUpRight size={18} />
            </Link>
            <a
              href="tel:+917091355794"
              data-testid="fees-cta-call"
              className="inline-flex items-center gap-2 rounded-full border border-sand/40 text-sand font-semibold px-8 py-4 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <Phone size={18} /> +91 70913 55794
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
